/**
 * THE ONE WAY TO START THIRD-PARTY WORK FROM A PAGE'S MOUNT: after
 * the page has painted, or after `load` has finished, whichever comes first.
 * Used by App's /login and Book's sign-in step to warm Firebase's popup
 * resolver, which injects https://apis.google.com/js/api.js, and by App's
 * AuthProvider to start App Check, which injects reCAPTCHA's api.js. App Check
 * is required, not optional: callers that need it before an Auth or API call
 * await `whenAppCheckStarted()` / `getAppCheckHeaderToken()` (lib/firebase).
 *
 * WHY. WebKit (Safari, and every iOS browser) holds the FIRST PAINT of a new
 * document until the page is "visually non-empty" or its load completes. A
 * short page like /login (134 visible characters and a 46x18 wordmark) sits
 * below that threshold, so it paints at load. A <script> injected while the
 * document is still loading becomes a load-delaying subresource; if a content
 * blocker DROPS it, `load` never fires, the hold never lifts, and the person
 * sees a blank white page while React works normally underneath (found in c2
 * on Dev, b6a58ba; docs/testing/release-v1.0.0-rc.1-2026-10-06.md).
 *
 * WHY THIS CANNOT HOLD FIRST PAINT. `task` runs only after one of:
 *
 *   - Two animation frames. WebKit services requestAnimationFrame only while
 *     it is rendering, so a second frame means the first one was committed:
 *     the page is already on screen, and nothing injected afterwards can take
 *     that back. While the hold is on, rAF does not fire at all, so this
 *     branch cannot win early.
 *   - `load`, and then a fresh task. NOT inside the load handler: WebKit
 *     decides the document has finished loading (which is what lifts the hold)
 *     only after the load event's dispatch returns, and a request started
 *     during that dispatch counts as still loading. The timeout puts the
 *     injection after that decision. If the document had already finished
 *     when this was called, the same fresh task applies.
 *
 * Whichever fires first runs `task` once; the other is cancelled. The returned
 * function cancels both (call it from the effect's cleanup), so a page that
 * unmounts before painting never starts the work on whatever route replaces
 * it.
 *
 * A hidden tab does not run animation frames, so there the work waits for
 * `load`. A caller that must not wait (a click) keeps its own direct path.
 */
export function afterFirstPaint(task: () => void, win: Window = window): () => void {
  let settled = false;
  let firstFrame: number | null = null;
  let secondFrame: number | null = null;
  let afterLoad: number | null = null;

  const stop = () => {
    settled = true;
    if (firstFrame !== null) win.cancelAnimationFrame(firstFrame);
    if (secondFrame !== null) win.cancelAnimationFrame(secondFrame);
    if (afterLoad !== null) win.clearTimeout(afterLoad);
    win.removeEventListener("load", onLoad);
  };

  const run = () => {
    if (settled) return;
    stop();
    task();
  };

  function onLoad() {
    if (settled || afterLoad !== null) return;
    afterLoad = win.setTimeout(run, 0);
  }

  if (win.document.readyState === "complete") onLoad();
  else win.addEventListener("load", onLoad);

  firstFrame = win.requestAnimationFrame(() => {
    firstFrame = null;
    if (settled) return;
    secondFrame = win.requestAnimationFrame(() => {
      secondFrame = null;
      run();
    });
  });

  return stop;
}
